import React, { memo, useCallback } from 'react';

import Form from 'react-bootstrap/Form';

import i18n from '../messages.json';

/**
 * Chip-list trigger that opens the CampaignPicker modal.
 * Clicking anywhere outside a chip's "×" opens the picker; clicking "×"
 * removes that single campaign without opening the picker.
 *
 * Props
 *   selectedCampaigns:     [{id, name}] — current confirmed selection
 *   campaignIdsCount:      number — currentMessage.campaignIds?.length ?? 0 (for required feedback)
 *   isCampaignsLoading:    boolean
 *   estimatesByCampaignId: { [id]: number | null } — per-segment estimated recipients
 *   isEstimateLoading:     boolean
 *   onOpenPicker:          () => void
 *   onRemoveCampaign:      (id) => void
 *
 * We show the estimate per segment only. There is intentionally no combined
 * total: summing segment sizes double-counts users in multiple segments and
 * doesn't reflect push opt-in, so a total would imply precision we don't have.
 */
// 'en-US' is pinned so grouping is stable regardless of the runtime locale
// (otherwise CI/prod locales could render "12 400" and diverge from tests).
const formatCount = (n) =>
  typeof n === 'number' ? `~${n.toLocaleString('en-US')}` : `~${i18n.ink.notification.campaign.estimate.unavailable}`;

const CampaignTriggerField = ({
  selectedCampaigns,
  campaignIdsCount,
  isCampaignsLoading,
  estimatesByCampaignId = {},
  isEstimateLoading = false,
  onOpenPicker,
  onRemoveCampaign,
}) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpenPicker();
      }
    },
    [onOpenPicker]
  );

  const handleChipRemove = useCallback(
    (e, id) => {
      e.stopPropagation();
      onRemoveCampaign(id);
    },
    [onRemoveCampaign]
  );

  return (
    <Form.Group controlId='formCampaigns'>
      <Form.Label className='notification-compose__field-label'>
        {i18n.ink.notification.campaign.label}
      </Form.Label>
      <div
        className='campaign-trigger'
        role='button'
        tabIndex={0}
        onClick={onOpenPicker}
        onKeyDown={handleKeyDown}
      >
        {selectedCampaigns.length === 0 ? (
          <span className='campaign-trigger__placeholder'>
            {i18n.ink.notification.campaign.placeholder}
          </span>
        ) : (
          <div className='campaign-trigger__chips'>
            {selectedCampaigns.map((c) => (
              <span key={c.id} className='campaign-chip' title={c.name}>
                <span className='campaign-chip__name'>{c.name}</span>
                <span className='campaign-chip__estimate'>
                  {isEstimateLoading ? '…' : formatCount(estimatesByCampaignId[c.id])}
                </span>
                <button
                  type='button'
                  className='campaign-chip__remove'
                  aria-label={`${i18n.ink.notification.campaign.removeAria}: ${c.name}`}
                  onClick={(e) => handleChipRemove(e, c.id)}
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      {isCampaignsLoading && (
        <Form.Text className='text-muted'>
          {i18n.ink.notification.loading}
        </Form.Text>
      )}
      {campaignIdsCount === 0 && (
        <div className='invalid-feedback d-block'>
          {i18n.ink.notification.campaign.required}
        </div>
      )}
    </Form.Group>
  );
};

export default memo(CampaignTriggerField);
